import { isNil } from 'lodash-es';

import { NonNullableObject } from '@rbi-ctg/frontend';
import { ICartEntry } from '@rbi-ctg/menu';
import { DriveThruLaneType } from '@rbi-ctg/store';
import { ILaunchDarklyCtx } from 'state/launchdarkly';
import { ServiceMode } from 'state/service-mode/types';
import { RBIPlatform } from 'utils/environment';
import { RBIExpandedPlatform } from 'utils/environment/types';

import { FALSE, FALSE_VALS, TRUE, TRUE_VALS } from './constants';
import { IMParticleAttributes } from './types';

export function serializeServiceMode(serviceMode: ServiceMode | null) {
  switch (serviceMode) {
    case ServiceMode.DELIVERY:
    case ServiceMode.CATERING_DELIVERY:
      return 'Delivery';
    case ServiceMode.DRIVE_THRU:
    case ServiceMode.EAT_IN:
    case ServiceMode.TAKEOUT:
    case ServiceMode.CURBSIDE:
    case ServiceMode.CATERING_PICKUP:
      return 'Pickup';
    default:
      return 'None';
  }
}

export function serializePickupMode(serviceMode: ServiceMode | null) {
  switch (serviceMode) {
    case ServiceMode.DELIVERY:
      return 'Delivery';
    case ServiceMode.DRIVE_THRU:
      return 'Drive Thru';
    case ServiceMode.EAT_IN:
      return 'Eat In';
    case ServiceMode.TAKEOUT:
      return 'Take Out';
    case ServiceMode.CURBSIDE:
      return 'Curbside';
    default:
      return 'None';
  }
}

export function serializePlatform(platform: RBIPlatform) {
  switch (platform) {
    case RBIPlatform.WEB:
      return 'Web';
    case RBIPlatform.APP:
      return 'Mobile App';
    case RBIPlatform.KIOSK:
      return 'Kiosk';
    default:
      return 'None';
  }
}

export function serializeDriveThruLaneType(driveThruLaneType: DriveThruLaneType | null) {
  switch (driveThruLaneType) {
    case 'single':
      return 'Single';
    case 'dual':
      return 'Lane';
    default:
      return '';
  }
}

export function serializeNumberOfDriveThruWindows(driveThruLaneType: DriveThruLaneType | null) {
  switch (driveThruLaneType) {
    case 'single':
      return 1;
    case 'dual':
      return 2;
    default:
      return 0;
  }
}

// Removes null, undefined and empty string values
export function sanitizeValues<M extends object = IMParticleAttributes>(
  attributes: M
): NonNullableObject<Partial<M>> {
  return Object.entries(attributes).reduce((memo, [key, value]) => {
    const attrIsEmptyString = typeof value === 'string' && value.length === 0;
    if (!isNil(value) && !attrIsEmptyString) {
      memo[key] = value;
    }
    return memo;
  }, {} as NonNullableObject<Partial<M>>) as NonNullableObject<Partial<M>>;
}

export const booleanToString = (bool: boolean): typeof TRUE | typeof FALSE => (bool ? TRUE : FALSE);

const normalizeStringBoolean = (str: string): string => {
  if (TRUE_VALS.includes(str)) {
    return TRUE;
  }
  if (FALSE_VALS.includes(str)) {
    return FALSE;
  }
  return str;
};

export function normalizeBooleans(attributes: object): any {
  const copy = {};
  Object.keys(attributes).forEach(key => {
    const value = attributes[key];
    if (typeof value === 'boolean') {
      copy[key] = booleanToString(value);
    } else if (typeof value === 'string') {
      copy[key] = normalizeStringBoolean(value);
    } else {
      copy[key] = value;
    }
  });
  return copy;
}

export function flattenCartEntryItems(cartEntry: ICartEntry): ICartEntry[] {
  const children = cartEntry.children.reduce((accum, current) => {
    return [...accum, ...flattenCartEntryItems(current)];
  }, []);
  return [cartEntry, ...children];
}

export function determinePlatformFromNavigator(): RBIExpandedPlatform {
  const regexPatterns = {
    androidRegex: /.*?Android.*?/i,
    iOSRegex: /^iP.*$/i
  };
  const androidMatch = regexPatterns.androidRegex.test(navigator.userAgent);
  const iOSMAtch = regexPatterns.iOSRegex.test(navigator.platform);

  if (androidMatch) {
    return RBIExpandedPlatform.ANDROID;
  } else if (iOSMAtch) {
    return RBIExpandedPlatform.IOS;
  }
  // default to Web if none of the above are matched
  return RBIExpandedPlatform.WEB;
}

/**
 * Normalizes LD flags that are having object values with LD flag's variation version
 * @param flags - Launch Darkly flags
 * @param getVariationDetail - function to get variation detail of a LD flag
 */
export function normalizeFlagsHavingObjectValues(
  flags: object,
  getVariationDetail: ILaunchDarklyCtx['getLdFlagVariationDetail']
): object {
  let updatedFlags = { ...flags };

  if (flags) {
    updatedFlags = Object.keys(flags).reduce((acc, flagName) => {
      acc[flagName] = flags[flagName];

      if (typeof flags[flagName] === 'object') {
        const variationDetail = getVariationDetail(flagName);

        if (variationDetail && typeof variationDetail.variationIndex === 'number') {
          acc[flagName] = `Variation ${variationDetail.variationIndex + 1}`;
        }
      }

      return acc;
    }, {});
  }

  return updatedFlags;
}
